/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import { useGetRemarkCodes } from "../../hooks/remark-code.queries";
import { IRemarkCode } from "../../types/remark-codes.types";
import { formatDate } from "../../../../utils/helpers";
import AddRemarkCode from "../AddRemarkCode/AddRemarkCode";
import { useDeleteRemarkCode } from "../../hooks/remark-code.mutations";
import MoreButton from "../../../../shared-ui/Buttons/MoreButton/MoreButton";
import useToast from "../../../../hooks/useToast";
import EditRemarkCode from "../EditRemarkCode/EditRemarkCode";

type Dialogs = "add-code" | "delete-code" | "edit-code";

export default function RemarksCodesList() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedCodeId, setSelectedCodeId] = React.useState<string | null>();

    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const searchString = searchParams.get("search") || "";

    const { data, error, isLoading, isSuccess } =
        useGetRemarkCodes(searchString);

    const remarkCodeToEdit = data?.data.find(
        (code) => code.remark_code_id === selectedCodeId
    );

    const deleteRemarkCode = useDeleteRemarkCode(selectedCodeId || "");

    const isAddingRemarkCode = useIsMutating(["add-remark-code"]);

    const isDeletingRemarkCode = useIsMutating(["delete-remark-code"]);

    const isEditingRemarkCode = useIsMutating(["edit-remark-code"]);

    const handleSearchStringChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        setSearchParams(searchParams);
    };

    const handleCloseDialog = () => {
        setSelectedCodeId(null);
        setDialogInView(null);
    };

    const handleDeleteRemarkCode = () => {
        deleteRemarkCode.mutate(null, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: ["remark-codes"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Remark code deleted successfully",
                });
                handleCloseDialog();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete remark code, please try again",
                });
            },
        });
    };

    const columns: ColumnDef<IRemarkCode>[] = [
        {
            accessorKey: "code",
            header: "Remark Code",
            cell: ({ row }) => (
                <span className="uppercase">{row.original.code}</span>
            ),
        },
        {
            accessorKey: "description",
            header: "DESCRIPTION",
            cell: ({ row }) => (
                <div className="uppercase whitespace-pre-wrap w-[500px]">
                    {row.original.description}
                </div>
            ),
        },
        {
            accessorKey: "created_at",
            header: "CREATED AT",
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            accessorKey: "updated_at",
            header: "UPDATED AT",
            cell: ({ row }) => formatDate(row.original.updated_at),
        },
        {
            accessorKey: "remark_code_id",
            header: "",
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownTrigger>
                        <MoreButton />
                    </DropdownTrigger>
                    <DropdownContent width="auto" align="end">
                        <DropdownItem
                            onClick={() => {
                                setSelectedCodeId(row.original.remark_code_id);
                                setDialogInView("edit-code");
                            }}
                        >
                            Edit
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => {
                                setSelectedCodeId(row.original.remark_code_id);
                                setDialogInView("delete-code");
                            }}
                        >
                            Delete
                        </DropdownItem>
                    </DropdownContent>
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            <Dialog open={dialogInView === "add-code"}>
                <DialogContent
                    variant="center"
                    title="Create Remark Code"
                    submitBtnFormValue="add-remark-code"
                    handleCloseDialog={() => handleCloseDialog()}
                    saveText="Add Code"
                    isSubmitBtnDisabled={isAddingRemarkCode > 0}
                    isCancelBtnDisabled={isAddingRemarkCode > 0}
                >
                    <AddRemarkCode onFormSubmit={() => handleCloseDialog()} />
                </DialogContent>
            </Dialog>

            {remarkCodeToEdit && (
                <Dialog open={dialogInView === "edit-code"}>
                    <DialogContent
                        variant="center"
                        title="Update Code"
                        submitBtnFormValue="edit-remark-code"
                        handleCloseDialog={() => handleCloseDialog()}
                        isSubmitBtnDisabled={isEditingRemarkCode > 0}
                        isCancelBtnDisabled={isEditingRemarkCode > 0}
                    >
                        <EditRemarkCode
                            codeToEdit={remarkCodeToEdit}
                            onFormSubmit={() => handleCloseDialog()}
                        />
                    </DialogContent>
                </Dialog>
            )}

            <AlertDialog open={dialogInView === "delete-code"}>
                <AlertDialogContent
                    onSaveClick={() => handleDeleteRemarkCode()}
                    saveText="Delete Code"
                    onCancelClick={() => handleCloseDialog()}
                    title="Delete Remark Code"
                    description="Are you sure you want to delete this remark code?"
                    isSaveBtnDisabled={isDeletingRemarkCode > 0}
                    isCancelBtnDisabled={isDeletingRemarkCode > 0}
                />
            </AlertDialog>

            <div className="rounded-r8 bg-white py-16 px-12 mb-32 flex items-center justify-between flex-wrap gap-y-12">
                <SearchInput
                    placeholder="Search..."
                    containerClass="w-[400px] max-w-full"
                    value={searchString}
                    onChange={handleSearchStringChange}
                />

                <Button onClick={() => setDialogInView("add-code")}>
                    Add Remark Code
                </Button>
            </div>

            <div className="mt-4 md:mt-12">
                {isLoading ? (
                    <Skeleton
                        type="table"
                        containerTestId="remark-codes-loader"
                        count={6}
                    />
                ) : null}

                {error && error?.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg="Cannot display remark codes at this time. Please try again later"
                    />
                )}
                {data?.data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Remark Codes will appear here once they are added"
                        isError={false}
                        emptyMessage="No remark codes added yet"
                    />
                )}
                {isSuccess && data?.data && data.data.length > 0 ? (
                    <div>
                        <Table columns={columns} data={data?.data} />
                    </div>
                ) : null}
            </div>
        </>
    );
}
